/**
 * CompanyProvider — CR1-FE-03
 *
 * Global context for the currently selected company.
 * Fetches companies from GET /api/companies and persists
 * the active selection in localStorage.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CompanyContextValue } from '@/types/company';
import { useCompanies } from '@/hooks/useCompanies';
import {
  setCompanyAccessDeniedHandler,
  setCompanyRequestSuccessHandler,
  setKnownCompanyIds,
} from '@/lib/queryClient';

export type { CompanyContextValue };

// Query keys invalidated on company switch — must match the queryKey[0]
// used in each hook (e.g. useQuery({ queryKey: ['/api/properties', …] })).
const COMPANY_DEPENDENT_KEYS = [
  '/api/properties', '/api/units',
  '/api/work-orders', '/api/users', '/api/admin/users', '/api/vendors',
] as const;

export const STORAGE_KEY = 'alpro:selectedCompanyId';

/**
 * Sentinel stored in localStorage when the SA explicitly clears the
 * selection to operate on the whole organization (SA-34 CA-03).
 *
 * Survives page reloads (vs. deleting the key, which would make
 * the auto-select logic re-pick `companies[0]` on the next mount).
 *
 * The middleware on the backend recognizes this exact value before the
 * UUID regex and short-circuits to `req.companyId = null` for super_admin,
 * allowing company-scoped controllers to read the full organization.
 */
export const ALL_ORGANIZATIONS_SENTINEL = '__all__';

// ─── Context ────────────────────────────────────────────────────────────────

export const CompanyContext = createContext<CompanyContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const queryClient = useQueryClient();
  const recoveryInProgressRef = useRef(false);

  const { data: companies = [], isLoading, error: queryError } = useCompanies();

  // Resolve initial selection from localStorage. The stored value may be a
  // company UUID or the `ALL_ORGANIZATIONS_SENTINEL` (SA explicitly cleared,
  // SA-34 CA-03). Both must survive a hard reload.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Derive the current company from the list.
  // SA-34 CA-03 (post-fix): the whole-org state is now persisted as the
  // sentinel `__all__` in localStorage, so a simple value check suffices
  // — no need for an in-memory `clearedByUser` flag (which used to be
  // lost on F5). When `selectedId` is the sentinel, `currentCompany` is
  // null and the request layer emits `X-Company-Id: __all__` so the
  // backend middleware can distinguish "whole org" from "unauthenticated
  // session" without resorting to the default-company fallback.
  // On initial load with NO localStorage value (PM first visit), auto-selects
  // the first accessible company (existing PM behavior).
  const currentCompany = useMemo(() => {
    if (companies.length === 0) {
      return null;
    }

    if (companies.length === 1 && selectedId !== ALL_ORGANIZATIONS_SENTINEL) {
      return companies[0];
    }

    if (selectedId === ALL_ORGANIZATIONS_SENTINEL) {
      // Whole-org view, persisted across reloads.
      return null;
    }

    if (selectedId === null) {
      // PM initial load with no stored choice: pick default or first company.
      return companies.find((c) => c.isDefault) ?? companies[0];
    }

    const match = companies.find((c) => c.id === selectedId);
    if (match) {
      return match;
    }

    return null;
  }, [companies, selectedId]);

  // Persist the selection. Pass `null` to clear (whole-org view), a UUID to
  // pick a specific company. The sentinel persists across reloads.
  const setCurrentCompany = useCallback(
    (companyId: string | null) => {
      setSelectedId(companyId);
      try {
        if (companyId === null) {
          localStorage.setItem(STORAGE_KEY, ALL_ORGANIZATIONS_SENTINEL);
        } else {
          localStorage.setItem(STORAGE_KEY, companyId);
        }
      } catch {
        // localStorage unavailable — ignore
      }
      // Invalidate company-dependent queries (NOT the companies list itself).
      // S3-FE-?? CA-1: cancel any in-flight fetch first. Without `cancelQueries`,
      // a previous (wrong-scope) request that completes after the invalidation
      // can still overwrite the cache, leaving the dependent query stuck on
      // the old company's data — same root cause as the CR1-FE-03 race.
      for (const key of COMPANY_DEPENDENT_KEYS) {
        void queryClient.cancelQueries({ queryKey: [key] });
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    [queryClient],
  );

  const recoverFromCompanyAccessDenied = useCallback(() => {
    if (recoveryInProgressRef.current) {
      return;
    }

    recoveryInProgressRef.current = true;

    // Wipe both the storage slot AND the in-memory state, then re-pick the
    // first company the user can actually access. We do NOT use
    // `setCurrentCompany` here because the invalidation it triggers would
    // race the recovery request.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable — ignore
    }
    setSelectedId(null);

    const fallbackCompany = companies[0];
    if (fallbackCompany) {
      setCurrentCompany(fallbackCompany.id);
      return;
    }

    for (const key of COMPANY_DEPENDENT_KEYS) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [companies, queryClient, setCurrentCompany]);

  const markCompanyRecoveryComplete = useCallback(() => {
    recoveryInProgressRef.current = false;
  }, []);

  // Derive error from query
  const error = queryError instanceof Error ? queryError : null;

  // Sync selectedId if the persisted value doesn't match any company.
  // Use setCurrentCompany (not manual setState) so company-dependent queries
  // are invalidated whenever the resolved company changes on mount.
  //
  // Whole-org sentinel (`__all__`) is treated as a valid persisted value:
  // `currentCompany === null` while `selectedId` is the sentinel is the
  // expected state (NOT a stale-id mismatch).
  useEffect(() => {
    if (!isLoading && companies.length > 0) {
      const persistedIsStaleUuid =
        selectedId !== null &&
        selectedId !== ALL_ORGANIZATIONS_SENTINEL &&
        !companies.some((c) => c.id === selectedId);

      if (persistedIsStaleUuid) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
        setSelectedId(null);
        return;
      }
      if (currentCompany && selectedId !== currentCompany.id) {
        setCurrentCompany(currentCompany.id);
      }
    }
  }, [isLoading, companies, currentCompany, selectedId, setCurrentCompany]);

  // CR1-FE-03 race-condition fix (E2E flake `admin should edit a property`):
  //
  // Page-mount race — `/api/companies` and `/api/properties` are queried in
  // parallel on every fresh navigation. The header-injection guard in
  // `queryClient.ts` only attaches `X-Company-Id` when `knownCompanyIds.has(localStorage)`
  // — but `knownCompanyIds` is populated by THIS effect, which fires AFTER
  // `/api/companies` returns. The dependent query therefore fires with no
  // header on the first render; the BE's `companyMiddleware` falls back to
  // the user's default company and returns its data (often empty for the
  // just-selected company). With `staleTime: Infinity`, that wrong-scope
  // response sticks in the cache until something else invalidates it.
  //
  // The fix: on the FIRST time `knownCompanyIds` is populated, invalidate
  // dependent queries so they refetch with the now-correct header. The
  // refetch adds ~1 RTT to the initial load but eliminates the wrong-scope
  // first paint that breaks `e2e/specs/company-property-flow.admin.spec.ts`.
  const knownCompanyIdsInitializedRef = useRef(false);
  useEffect(() => {
    if (isLoading) {return;}
    setKnownCompanyIds(companies.map((company) => company.id));
    // S3-FE-?? CA-1 fix: only mark initialized once we have REAL company
    // ids. The previous condition would mark the ref on the first non-loading
    // render even if `companies === []`, so when the actual data arrived on
    // a later tick, the dependent-query invalidation never fired and
    // `/api/properties` was stuck on its first wrong-scope response.
    if (!knownCompanyIdsInitializedRef.current && companies.length > 0) {
      knownCompanyIdsInitializedRef.current = true;
      // S3-FE-?? CA-1: cancel in-flight fetches BEFORE invalidating. Dependent
      // queries (`/api/properties`, etc.) often start their first request
      // before `knownCompanyIds` is populated, so they fire WITHOUT the
      // `X-Company-Id` header. If that first request completes AFTER the
      // invalidation-triggered refetch, it overwrites the cache with the
      // wrong-scope response and the table sticks on the previous company's
      // data. Cancelling first guarantees only the post-header fetch lands.
      for (const key of COMPANY_DEPENDENT_KEYS) {
        void queryClient.cancelQueries({ queryKey: [key] });
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
  }, [companies, isLoading, queryClient]);

  // S3-FE-?? CA-1: the previous safety-net (`emptyCompaniesRecoveryRef`)
  // that invalidated `/api/companies` once when `companies.length === 0`
  // has been removed. It was a band-aid over a symptom — a one-shot retry
  // that could itself land empty and leave the selector stuck. The real
  // fix lives at the auth boundary in `Login.tsx` (`removeQueries` after
  // `clear()`) and in `useCompanies` (`staleTime: 0` +
  // `refetchOnMount: 'always'`). Together they make the post-login state
  // deterministic without needing a per-render recovery hook.

  useEffect(() => {
    setCompanyAccessDeniedHandler(recoverFromCompanyAccessDenied);
    setCompanyRequestSuccessHandler(markCompanyRecoveryComplete);

    return () => {
      setCompanyAccessDeniedHandler(null);
      setCompanyRequestSuccessHandler(null);
    };
  }, [markCompanyRecoveryComplete, recoverFromCompanyAccessDenied]);

  const value = useMemo<CompanyContextValue>(
    () => ({
      companies,
      currentCompany,
      setCurrentCompany,
      isLoading,
      error,
    }),
    [companies, currentCompany, setCurrentCompany, isLoading, error],
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useCompany(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error('useCompany must be used within a <CompanyProvider>');
  }
  return ctx;
}
